import { useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Typography,
} from '@mui/material'
import Joi from 'joi'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { ConfirmDialog, ControlledTextField, FplDialogTitle } from 'components/common'
import {
  WebhookSubscriptionDetailsFragment,
  useCreateWebhookSubscriptionMutation,
  useUpdateWebhookSubscriptionMutation,
} from 'generated/graphql'

import {
  CALLBACK_URL_MAX_LENGTH,
  WEBHOOK_EVENT_TYPES,
  isAllowedCallbackUrl,
} from './webhookConstants'

interface IFormValues {
  callbackUrl: string
  eventTypes: Array<string>
}

const validationSchema = Joi.object<IFormValues>({
  callbackUrl: Joi.string()
    .label('Callback URL')
    .trim()
    .max(CALLBACK_URL_MAX_LENGTH)
    .required()
    .custom((value, helpers) =>
      isAllowedCallbackUrl(value) ? value : helpers.error('any.invalid'),
    )
    .messages({
      'any.invalid':
        'Callback URL must be an HTTPS URL on port 443 without credentials, query string or fragment',
    }),
  eventTypes: Joi.array()
    .items(Joi.string().valid(...WEBHOOK_EVENT_TYPES.map((x) => x.value)))
    .min(1)
    .required()
    .messages({ 'array.min': 'Select at least one event type' }),
})

interface IProps {
  customerId: number
  subscription: WebhookSubscriptionDetailsFragment | null
  openDialog: boolean
  onCloseDialog: () => void
}

const refetchQueries = ['GetPagedWebhookSubscriptions']

const WebhookSubscriptionDialog = (props: IProps) => {
  const { customerId, subscription, openDialog, onCloseDialog } = props
  const isUpdate = !!subscription

  const [pendingValues, setPendingValues] = useState<IFormValues | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      callbackUrl: subscription?.callbackUrl ?? '',
      eventTypes: subscription?.eventTypes ?? WEBHOOK_EVENT_TYPES.map((x) => x.value),
    },
  })

  const [createWebhookSubscription, { loading: creating }] = useCreateWebhookSubscriptionMutation({
    refetchQueries,
    onCompleted: () => {
      toast.success('Webhook subscription was created successfully.')
      onCloseDialog()
    },
  })

  const [updateWebhookSubscription, { loading: updating }] = useUpdateWebhookSubscriptionMutation({
    refetchQueries,
    onCompleted: () => {
      toast.success('Webhook subscription was updated successfully.')
      onCloseDialog()
    },
  })

  const onSubmit: SubmitHandler<IFormValues> = (values) => {
    setPendingValues(values)
  }

  const handleConfirmSave = () => {
    if (!pendingValues) return

    const callbackUrl = pendingValues.callbackUrl.trim()

    if (subscription) {
      updateWebhookSubscription({
        variables: {
          input: { id: Number(subscription.id), callbackUrl, eventTypes: pendingValues.eventTypes },
        },
      })
    } else {
      createWebhookSubscription({
        variables: {
          input: { customerId, callbackUrl, eventTypes: pendingValues.eventTypes },
        },
      })
    }
  }

  const isProcessing = creating || updating

  return (
    <Dialog fullWidth maxWidth='sm' open={openDialog} aria-labelledby='webhook-subscription-dialog'>
      <FplDialogTitle id='webhook-subscription-dialog' onClose={onCloseDialog}>
        <Typography paragraph variant='h4'>
          {isUpdate ? 'Update' : 'Create'} Webhook Subscription
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <Box pt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name='callbackUrl'
                label='Callback URL'
                inputMode='url'
                maxLength={CALLBACK_URL_MAX_LENGTH}
                required
                error={!!errors.callbackUrl}
                helperText={errors.callbackUrl?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl required error={!!errors.eventTypes} component='fieldset'>
                <FormLabel component='legend'>Event types</FormLabel>
                <Controller
                  control={control}
                  name='eventTypes'
                  render={({ field: { value, onChange } }) => (
                    <FormGroup>
                      {WEBHOOK_EVENT_TYPES.map((eventType) => (
                        <FormControlLabel
                          key={eventType.value}
                          label={`${eventType.label} (${eventType.value})`}
                          control={
                            <Checkbox
                              checked={value.includes(eventType.value)}
                              onChange={(_, checked) =>
                                onChange(
                                  checked
                                    ? [...value, eventType.value]
                                    : value.filter((x) => x !== eventType.value),
                                )
                              }
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                {errors.eventTypes && (
                  <FormHelperText>{(errors.eventTypes as any).message}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={onCloseDialog}>
          Cancel
        </Button>
        <LoadingButton variant='contained' loading={isProcessing} onClick={handleSubmit(onSubmit)}>
          Save
        </LoadingButton>
      </DialogActions>

      <ConfirmDialog
        title='Confirm webhook destination'
        open={!!pendingValues}
        setOpen={(open: boolean) => !open && setPendingValues(null)}
        onConfirm={handleConfirmSave}>
        <DialogContentText>
          Booking event data for this customer will be sent to:
          <Box component='span' display='block' mt={1} sx={{ wordBreak: 'break-all' }}>
            <strong>{pendingValues?.callbackUrl.trim()}</strong>
          </Box>
        </DialogContentText>
      </ConfirmDialog>
    </Dialog>
  )
}

export default WebhookSubscriptionDialog
